import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

interface SignaturePadProps {
  onChange: (blob: Blob | null) => void;
  className?: string;
}

/** Minimal on-site signature capture: draw on a canvas, export a PNG blob.
 *  No external library — pointer events draw strokes; Clear resets. */
export function SignaturePad({ onChange, className }: SignaturePadProps) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);

  const ctx = () => canvasRef.current?.getContext('2d') ?? null;

  const pos = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // The canvas is stretched by CSS (w-full) while its internal resolution is
    // fixed (width/height attrs), so pointer coords (CSS px) must be scaled into
    // canvas-space — otherwise the stroke lands offset from the cursor.
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * sx,
      y: (e.clientY - rect.top) * sy,
    };
  };

  const start = (e: React.PointerEvent) => {
    const c = ctx();
    if (!c) return;
    drawing.current = true;
    // Always a dark stroke on the white pad — a signature is ink on paper,
    // independent of the app theme.
    c.strokeStyle = 'black';
    const { x, y } = pos(e);
    c.beginPath();
    c.moveTo(x, y);
  };

  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const c = ctx();
    if (!c) return;
    const { x, y } = pos(e);
    c.lineTo(x, y);
    c.lineWidth = 2;
    c.lineCap = 'round';
    c.stroke();
    setDirty(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (!canvas || !canvas.toBlob) return;
    canvas.toBlob(b => onChange(b), 'image/png');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const c = ctx();
    if (canvas && c) c.clearRect(0, 0, canvas.width, canvas.height);
    setDirty(false);
    onChange(null);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <canvas
        ref={canvasRef}
        width={300}
        height={140}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        className="w-full rounded-md border border-input bg-white touch-none"
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {dirty ? t('inspection.signed') : t('inspection.signHere')}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={clear}>
          {t('common.clear')}
        </Button>
      </div>
    </div>
  );
}
